/* eslint-disable react-native/no-inline-styles */
import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { View, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { debounce } from 'lodash';

import { Loader } from '@components/screen/Layout';
import SociollaText from '@components/sociolla/SociollaText';
import Image from '@components/global/Image';
import SearchInputMobile from '@components/global/SearchInputMobile';

import Colors from '@global/colors';
import { fullWidth, fullHeight } from '@global/screens';
import { paginationAdded } from '@/store/params';

import PropTypes from 'prop-types';

const propTypes = {
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  name: PropTypes.string,
  placeholder: PropTypes.string,
  loading: PropTypes.bool,
  data: PropTypes.array,
  hasFilter: PropTypes.bool,
  onSearch: PropTypes.func,
  renderItem: PropTypes.func,
  infoHeight: PropTypes.number,
  headerComponent: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
  onFilter: () => {},
  withLoadMore: PropTypes.bool,
  withFetch: PropTypes.bool,
  loadingPagination: PropTypes.bool,
  skip: PropTypes.number,
  limit: PropTypes.number,
  totalData: PropTypes.number,
  onLoadMore: PropTypes.func,
};

const defaultProps = {
  name: '',
  placeholder: 'Search something...',
  loading: false,
  data: [],
  renderItem: ({ data, index }) => {},
  style: {},
  infoHeight: 0,
  hasFilter: false,
  headerComponent: false,
  onSearch: () => {},
  onFilter: () => {},
  withLoadMore: false,
  withFetch: false,
  loadingPagination: false,
  skip: 0,
  limit: 10,
  totalData: 0,
  onLoadMore: () => {},
};

const ListProductBrand = (props) => {
  const dispatch = useDispatch();
  const insets = useSafeAreaInsets();
  const height = fullHeight - insets.top - 55;
  const [isSticky, setIsSticky] = useState(false);
  const {
    loading = false,
    loadingPagination = false,
    data = [],
    renderItem = ({ data, index }) => {},
    style = {},
    infoHeight = 0,
    hasFilter = false,
    headerComponent = false,
    onSearch = () => {},
    onFilter = () => {},
    withLoadMore = false,
    withFetch = false,
    skip = 0,
    limit = 10,
    totalData = 0,
    onLoadMore = () => {},
  } = props;

  const calculatePage = Math.round(totalData / limit);
  const pageCount = calculatePage ? calculatePage : 1;
  const calculatePageNumber = skip / limit + 1;
  const pageNumber = calculatePageNumber ? calculatePageNumber : 1;

  const isCloseToBottom = ({
    layoutMeasurement,
    contentOffset,
    contentSize,
  }) => {
    const paddingToBottom = 50;
    return (
      layoutMeasurement.height + contentOffset.y >=
      contentSize.height - paddingToBottom
    );
  };

  const onLoadMoreHandler = () => {
    if (!loadingPagination) {
      if (pageCount >= pageNumber && totalData > limit && skip <= totalData) {
        const newSkip = skip + limit;
        try {
          if (withFetch) {
            dispatch(paginationAdded({ skip: newSkip, limit: limit }));
          }
          onLoadMore(newSkip);
        } catch (error) {}
      }
    }
  };

  const doSearch = useCallback(
    debounce((keyword) => {
      onSearch(keyword);
    }, 500),
    [],
  );

  return (
    <ScrollView
      {...props}
      showsVerticalScrollIndicator={false}
      style={[{ height }, style]}
      stickyHeaderIndices={isSticky ? [1] : []}
      scrollEventThrottle={infoHeight ? infoHeight : 200}
      onScroll={({ nativeEvent }) => {
        if (nativeEvent?.contentOffset?.y >= infoHeight ? infoHeight : 200) {
          if (!isSticky) {
            setIsSticky(true);
          }
        } else {
          if (isSticky) {
            setIsSticky(false);
          }
        }
        if (withLoadMore && isCloseToBottom(nativeEvent)) {
          onLoadMoreHandler();
        }
      }}>
      {headerComponent ? headerComponent : false}
      <View style={{ zIndex: 1001, elevation: 1001 }}>
        <View
          style={{
            width: '100%',
            flexDirection: 'row',
            borderBottomColor: Colors.grey0,
            borderBottomWidth: 1,
          }}>
          <View style={{ width: fullWidth - 55 }}>
            <SearchInputMobile
              searchKey={'name'}
              noBorderBottom={true}
              placeholder={
                props.placeholder ? props.placeholder : 'Search something...'
              }
              // onFocus={() => onFocusSearch()}
              // onBlur={() => onBlurSearch()}
              // value={searchKeyword}
              onSearch={async (value) => {
                // await setSearchKeyword(value);
                doSearch(value);
              }}
            />
          </View>
          <View style={styles.filterIconContainer}>
            <TouchableOpacity
              activeOpacity={0.8}
              style={styles.filterIconWrapper}
              onPress={() => {
                onFilter();
              }}>
              <Image
                style={{ width: 20, height: 20 }}
                width={20}
                height={20}
                svg={true}
                source={require('@assets/img/icons/sort.svg')}
              />
              {hasFilter ? (
                <View style={styles.hasFilter}>
                  <Ionicons name="checkmark" size={11} color={Colors.white} />
                </View>
              ) : (
                false
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
      {loading ? (
        <Loader />
      ) : data?.length ? (
        data.map((item, idx) => renderItem({ data: item, index: idx }))
      ) : (
        <View style={styles.noData}>
          <SociollaText style={styles.noDataText}>No data found</SociollaText>
        </View>
      )}
      {loadingPagination ? <Loader /> : false}
      <View style={{ height: insets.bottom + 20 }} />
    </ScrollView>
  );
};

ListProductBrand.propTypes = propTypes;
ListProductBrand.defaultProps = defaultProps;

const styles = StyleSheet.create({
  noData: {
    width: '100%',
    paddingHorizontal: 15,
    paddingVertical: 25,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  noDataText: { fontSize: 13 },
  filterIconContainer: {
    width: 55,
    backgroundColor: Colors.white,
  },
  filterIconWrapper: {
    width: 45,
    height: 45,
    marginVertical: 10,
    backgroundColor: Colors.grey3,
    borderRadius: 4,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  hasFilter: {
    position: 'absolute',
    bottom: 10,
    right: 10,
    width: 13,
    height: 13,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.blue0,
    borderRadius: 15,
  },
});

export default React.memo(ListProductBrand, (prev, next) => prev == next);
